// mediaTagging/TagSaveQueue.js — the save queue of a tagging session (plan bugfix_2026-10-06_permission-tagging,
// phase speichern, stage 1a-2). Pure: no DOM, no mediator.
//
// What it guarantees, unlike the old TaggingCore queue (analysis W2–W4):
// - ONE change per box; prev stays the state the server knows.
// - Only sendable boxes go out (isSendable); the others stay in memory — never dropped silently.
// - Changes are bundled: scheduleFlush() waits SAVE_BUNDLE_DELAY_MS, flushNow() sends at once.
// - At most ONE batch is on its way. A box that changes while its batch is in flight is sent again afterwards —
//   with the real tag id, never as a second "new".
// - A failed change stays queued (onFailed) and is retried by the next flush, not in the same one. It is marked
//   failed until a send of it succeeds (pending()[i].failed; onSaved/onFailed get `failed` = the mark BEFORE this send).

export const SAVE_BUNDLE_DELAY_MS = 500;   // started at 1000; 500 kept after the trial — feels like before (User 2026-10-06)

export class TagSaveQueue {
    #pending = [];          // [{ tagId, type: 'save' | 'delete', prev, model, version, failed }]
    #sendBatch;
    #isSendable;
    #onSaved;
    #onFailed;
    #delayMs;
    #setTimer;
    #clearTimer;
    #timer = null;
    #inFlight = null;       // Promise of the batch on its way
    #inFlightEntries = new Set();
    #getExcept;

    constructor({
        sendBatch,                      // (changes) => Promise<[{ ok, tagId?, response? }]>, one result per change, same order
        isSendable = () => true,        // (change) => boolean, only asked for saves — a delete is always sendable
        onSaved = () => {},             // (change, result) => void
        onFailed = () => {},            // (change, result) => void
        delayMs = SAVE_BUNDLE_DELAY_MS,
        setTimer = (fn, ms) => setTimeout(fn, ms),
        clearTimer = (t) => clearTimeout(t),
        getExcept = () => null,         // () => tagId — the box a SCHEDULED flush leaves out (the one still being edited)
    }) {
        this.#getExcept = getExcept;
        this.#sendBatch = sendBatch;
        this.#isSendable = isSendable;
        this.#onSaved = onSaved;
        this.#onFailed = onFailed;
        this.#delayMs = delayMs;
        this.#setTimer = setTimer;
        this.#clearTimer = clearTimer;
    }

    // One entry per box: prev stays, model follows the latest state. Created-then-deleted (prev null) vanishes.
    upsert(change) {
        const existing = this.#pending.find((c) => c.tagId === change.tagId);
        if (!existing) {
            this.#pending.push({ tagId: change.tagId, type: change.type, prev: change.prev ?? null, model: change.model, version: 1, failed: false });
            return;
        }
        // Created and deleted before the server ever saw it: nothing to send. But if its create is on its way, the box
        // WILL exist on the server — keep the entry as a delete; after the create it goes out with the real id.
        if (change.type === 'delete' && existing.prev === null && !this.#inFlightEntries.has(existing)) {
            this.#pending.splice(this.#pending.indexOf(existing), 1);
            return;
        }
        existing.type = change.type;
        existing.model = change.model;
        existing.version += 1;
    }

    // A placeholder became a real tag id — rename what is queued.
    rekey(oldId, newId) {
        for (const c of this.#pending) {
            if (c.tagId !== oldId) { continue; }
            c.tagId = newId;
            c.model = { ...c.model, id: newId };
            if (c.prev) { c.prev = { ...c.prev, id: newId }; }
        }
    }

    pending()     { return this.#pending.map((c) => ({ ...c })); }
    clear()       { this.#pending = []; this.#cancelTimer(); }

    // Send after the bundle delay; another call restarts the delay.
    scheduleFlush() {
        this.#cancelTimer();
        this.#timer = this.#setTimer(() => { this.#timer = null; this.flushNow({ except: this.#getExcept() }); }, this.#delayMs);
    }

    // Send every sendable change (except the box still being edited) — one batch at a time, until nothing sendable is
    // left. Resolves with { saved } when done. Changes that fail in this call are not retried by it.
    async flushNow({ except = null } = {}) {
        this.#cancelTimer();
        const failedNow = new Set();
        let saved = false;   // sent a round here, or waited for one already on its way — both flash the boxes
        for (;;) {
            if (this.#inFlight) { saved = true; await this.#inFlight; continue; }
            const batch = this.#pending.filter((c) => c.tagId !== except && !failedNow.has(c) && this.#canSend(c));
            if (!batch.length) { return { saved }; }
            saved = true;
            const round = this.#sendRound(batch, failedNow);
            this.#inFlight = round;
            try { await round; } finally { if (this.#inFlight === round) { this.#inFlight = null; } }
        }
    }

    #canSend(c) { return c.type === 'delete' || this.#isSendable(c); }

    #cancelTimer() {
        if (this.#timer !== null) { this.#clearTimer(this.#timer); this.#timer = null; }
    }

    async #sendRound(batch, failedNow) {
        const snapshots = batch.map((c) => ({ tagId: c.tagId, type: c.type, prev: c.prev, model: c.model, version: c.version, failed: c.failed }));
        batch.forEach((c) => this.#inFlightEntries.add(c));
        let results;
        try {
            results = await this.#sendBatch(snapshots.map(({ version, failed, ...change }) => change));
        } catch (err) {
            results = snapshots.map(() => ({ ok: false, error: err }));
        } finally {
            batch.forEach((c) => this.#inFlightEntries.delete(c));
        }
        snapshots.forEach((snap, i) => {
            const entry = batch[i];
            const result = results?.[i] ?? { ok: false };
            if (result.ok) {
                entry.failed = false;
                this.#onSaved(snap, result);        // may rekey — entry.tagId is up to date afterwards
                if (entry.version === snap.version || snap.type === 'delete') {
                    this.#remove(entry);
                } else {
                    // changed while in flight: the saved state is what the server knows now → the next save updates it
                    entry.prev = { ...snap.model, id: entry.tagId };
                }
            } else {
                failedNow.add(entry);
                entry.failed = true;
                this.#onFailed(snap, result);
            }
        });
    }

    #remove(entry) {
        const i = this.#pending.indexOf(entry);
        if (i !== -1) { this.#pending.splice(i, 1); }
    }
}

// Stage 1 transport: sends a batch change by change over the single-change persistence (persistence.save/delete
// with onOk/onErr callbacks, as documents.js provides it) — one after the other, never in parallel.
export function createSequentialBatchSender({ save, delete: remove }) {
    const sendOne = (change) => new Promise((resolve) => {
        // No response at all = the legacy persistence found nothing to save (storeChangedTag answers callbackOK(null)
        // when the change normalises away) — that is a success, not a failure to retry.
        const onOk  = (response) => resolve({ ok: response == null || response.success === true, tagId: Number(response?.tagId) || undefined, response });
        const onErr = (response) => resolve({ ok: false, response });
        if (change.type === 'delete') { remove(change.model, onOk, onErr); }
        else { save(change.prev, change.model, onOk, onErr); }
    });
    return async (changes) => {
        const results = [];
        for (const change of changes) { results.push(await sendOne(change)); }
        return results;
    };
}
